"use client";
import Footer from "@/components/footer";
import Navbar from "@/components/navbar";
import Image from "next/image";
import { Title, Paragraph } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { ChevronRight } from "lucide-react";

import React, { useEffect } from "react";

import HomeBanner from "@/components/homeBanner";

import useProducts from "./hooks/useProducts";

import { useRouter } from "next/navigation";

import NewsletterSection from "@/app/_components/SubscribeNewsletter";

// query setup strapi

import ProductBanner from "./_components/ProductBanner";
import { getCookieValue, normalizeProductData } from "./utils/utility";
import HomePageLoader from "./_components/HomePageLoader";
import FeatureBrands from "./_components/FeatureBrands";
import Animate from "@/components/Animate";
import FeatureProduct from "./_components/FeatureProduct";
import { ParallaxWithFramer } from "@/components/Parallax";
import Section from "./_components/Section";

import useSmoothScroll from "@/hooks/useSmoothScroll";
import useSession from "@/hooks/useSession";
import ApartSection from "./_components/ApartSection";
import UnlockExclusive from "./_components/UnlockExclusive";
import useHomeData from "@/cms_data/useHomeData";
import { arrayValidator } from "./utils/validator";
import { cmsUrl } from "./config";

export default function Home() {
  useSmoothScroll();
  useSession();
  const { brandList, newArrivalList } = useProducts();
  const newProductList = normalizeProductData(newArrivalList || []);
  const router = useRouter();
  const { pageData, isLoading } = useHomeData();
  const token = getCookieValue("accessToken");

  if (isLoading)
    return (
      <>
        <Navbar />
        <HomePageLoader />
      </>
    );

  return (
    <>
      <Navbar promotion={true} />

      <main className="flex min-h-screen flex-col overflow-x-hidden">
        <div className="w-full">
          <HomeBanner homePageBanner={pageData} />
        </div>

        <Section>
          <ProductBanner title="Popular Products" productList={newProductList} />
        </Section>

        {/* what set us apart */}
        <Section>
          <ApartSection homePage={pageData} />
        </Section>

        {/* New Arrivals */}

        <Section>
          <ProductBanner productList={newProductList} title="New Arrivals" />
        </Section>

        {/* Unlock exclusive deals */}
        <UnlockExclusive homePage={pageData} />

        {/* Feature Brands */}
        <Section>
          <div className=" text-center ">
            <Animate animation="fadeInUp" duration={1}>
              <Title size="lg" className="center">
                {pageData?.header}
              </Title>
              <Paragraph size="s" className="mt-[14px] font-light max-w-[500px] mx-auto text-midnightIndigo-300">
                {pageData?.subHeader}
              </Paragraph>
            </Animate>

            <FeatureBrands images={pageData?.images} />
          </div>
        </Section>

        <Section>
          <ParallaxWithFramer threshold={[0.7, 1]}>
            <div className="w-full">
              <div className="md:flex mx-auto">
                <div className="flex flex-wrap w-full">
                  <FeatureProduct
                    productDetails={pageData && arrayValidator(pageData.promotions, 0) ? pageData.promotions[0] : ""}
                    className={"lg:h-[351px] h-[320px] 2xl:h-[24.33vw] basis-full lg:basis-1/3 md:basis-1/2"}
                  />
                  <FeatureProduct
                    productDetails={pageData && arrayValidator(pageData.promotions, 1) ? pageData.promotions[1] : ""}
                    className={"lg:h-[480px] h-[320px] 2xl:h-[33.33vw] bg-cover basis-full lg:basis-1/3 md:basis-1/2 "}
                  />
                  <FeatureProduct
                    productDetails={pageData && arrayValidator(pageData.promotions, 2) ? pageData.promotions[2] : ""}
                    className={"lg:h-[609px] h-[320px] 2xl:h-[42.30vw] basis-full lg:basis-1/3 md:basis-1/2"}
                  />

                  <FeatureProduct
                    productDetails={pageData && arrayValidator(pageData.promotions, 3) ? pageData.promotions[3] : ""}
                    className={
                      "lg:h-[609px] h-[320px] 2xl:h-[42.29vw] basis-full lg:basis-1/3 md:basis-1/2 lg:mt-[-260px] 2xl:mt-[-18.04vw] "
                    }
                  />

                  <FeatureProduct
                    productDetails={pageData && arrayValidator(pageData.promotions, 4) ? pageData.promotions[4] : ""}
                    className={
                      "h-[320px]  lg:h-[480px] 2xl:h-[33.35vw] basis-full md:basis-1/2 lg:basis-1/3  lg:mt-[-130px] 2xl:mt-[-9.02vw] "
                    }
                  />

                  <FeatureProduct
                    productDetails={pageData && arrayValidator(pageData.promotions, 5) ? pageData.promotions[5] : ""}
                    className={"lg:h-[351px] h-[320px] 2xl:h-[24.3vw] basis-full lg:basis-1/3 md:basis-1/2"}
                  />
                </div>
              </div>
            </div>
          </ParallaxWithFramer>
        </Section>

        {/* Special offers */}
        <Section>
          <ProductBanner productList={newProductList} title="Special Offers" />
        </Section>

        <ParallaxWithFramer threshold={[0.5, 0.8]}>
          <Animate delay={0.5}>
            <div className="w-full    ">
              <div className=" grid lg:grid-rows-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                <Animate className="w-full  h-[340px] md:h-[385px] lg:h-[680px] 2xl:h-[46.67vw] relative  lg:row-span-2 lg:col-span-2 ">
                  <div className="absolute w-full flex flex-col lg:flex-row lg:justify-between p-[24px] lg:p-[1.6vw]">
                    <div className=" mb-3 md:w-[366px] lg:w-[410px]  xl:w-[28.43vw] ">
                      <Title size="lg" className="text-system-disabled ">
                        {pageData && arrayValidator(pageData.sales, 0) ? pageData?.sales[0]?.saleTitle : ""}
                      </Title>
                      <Paragraph className="text-system-disabled mt-[14px] font-light max-w-[92%]">
                        {pageData && arrayValidator(pageData.sales, 0) ? pageData?.sales[0]?.saleSubtitle : ""}
                      </Paragraph>

                      <Button
                        type="submit"
                        className="mt-[24px] w-[126px] 2xl:w-[8.7vw]"
                        onClick={() => {
                          const url =
                            pageData && arrayValidator(pageData.sales, 0)
                              ? `/${pageData?.sales[0]?.saleButtonLink}`
                              : "/";
                          router.push(url);
                        }}
                      >
                        {pageData && arrayValidator(pageData.sales, 0) ? pageData?.sales[0]?.saleButtonText : ""}
                      </Button>
                    </div>
                    <span className=" text-system-titanium text-[13px] lg:text-[15px] xl:text-[1.04vw] font-light block ">
                      {/* {homePromoDeal?.dealsImages[0]?.time} */}
                    </span>
                  </div>

                  <Image
                    height={680}
                    width={720}
                    className="w-full h-full object-cover "
                    alt={pageData && arrayValidator(pageData.sales, 0) ? pageData?.sales[0]?.saleTitle : ""}
                    src={
                      pageData && arrayValidator(pageData.sales, 0)
                        ? `${cmsUrl}/${pageData?.sales[0]?.image?.url ? pageData?.sales[0]?.image?.url : ""}`
                        : ""
                    }
                  />
                </Animate>

                <Animate
                  delay={0.5}
                  className="w-full h-[340px] md:h-[385px] lg:h-[340px] 2xl:h-[23.33vw] relative lg:row-span-1 lg:col-span-2"
                >
                  <div className="absolute p-[24px] lg:p-[1.6vw]">
                    <Title size="lg" className=" leading-[120%] max-w-[80vw] lg:max-w-[40vw] ">
                      {pageData && arrayValidator(pageData.sales, 1) ? pageData?.sales[1]?.saleTitle : ""}
                    </Title>
                    <Paragraph size="s" className="mt-[14px] font-light max-w-[70%] ">
                      {pageData && arrayValidator(pageData.sales, 1) ? pageData?.sales[1]?.saleSubtitle : ""}
                    </Paragraph>
                    <Link
                      href={
                        pageData && arrayValidator(pageData.sales, 1) ? `/${pageData?.sales[1]?.saleButtonLink}` : "/"
                      }
                      className="inline-flex  items-center text-system-primary mt-[12px] xl:text-[1.11vw]   hoverunderline hoverunderline-blue"
                    >
                      {pageData && arrayValidator(pageData.sales, 1) ? pageData?.sales[1]?.saleButtonText : ""}
                      <ChevronRight className=" h-[20px] w-[20px] xl:h-[1.18vw] xl:w-[1.04vw]" />
                    </Link>
                  </div>
                  <Image
                    height={340}
                    width={720}
                    className="w-full h-full object-cover"
                    alt={pageData && arrayValidator(pageData.sales, 1) ? pageData?.sales[1]?.saleTitle : ""}
                    src={
                      pageData && arrayValidator(pageData.sales, 1) && pageData?.sales[1]?.image?.url
                        ? `${cmsUrl}/${pageData?.sales[1]?.image?.url ? pageData?.sales[1]?.image?.url : ""}`
                        : ""
                    }
                  />
                </Animate>

                <Animate delay={0.8} className="w-full h-[340px] md:h-[290px] lg:h-[340px] 2xl:h-[23.33vw] relative ">
                  <div className="absolute bottom-[24px] left-[24px]  lg:bottom-[1.6vw] lg:left-[1.6vw] ">
                    <Title className="leading-[120%] !text-[24px] max-w-[90%]">
                      {pageData && arrayValidator(pageData.sales, 2) ? pageData?.sales[2]?.saleTitle : ""}
                    </Title>
                    <Link
                      href={
                        pageData && arrayValidator(pageData.sales, 2) ? `/${pageData?.sales[2]?.saleButtonLink}` : "/"
                      }
                      className="inline-flex  items-center text-system-primary mt-[12px] xl:text-[1.11vw] hoverunderline hoverunderline-blue"
                    >
                      {pageData && arrayValidator(pageData.sales, 2) ? pageData?.sales[2]?.saleButtonText : ""}
                      <ChevronRight className="ml-1 h-[17px] w-[15px] xl:h-[1.18vw] xl:w-[1.04vw]" />
                    </Link>
                  </div>
                  <Image
                    height={340}
                    width={360}
                    className="w-full h-full object-cover"
                    alt={pageData && arrayValidator(pageData.sales, 2) ? pageData?.sales[2]?.saleTitle : ""}
                    src={
                      pageData && arrayValidator(pageData.sales, 2) && pageData?.sales[2]?.image?.url
                        ? `${cmsUrl}/${pageData?.sales[2]?.image?.url ? pageData?.sales[2]?.image?.url : ""}`
                        : ""
                    }
                  />
                </Animate>

                <Animate delay={1} className="w-full h-[340px] relative md:h-[290px] lg:h-[340px] 2xl:h-[23.33vw] ">
                  <div className="absolute bottom-[24px] left-[24px] lg:bottom-[1.6vw] lg:left-[1.6vw] ">
                    <Title className="text-system-disabled max-w-[90%] leading-[120%] !text-[24px]">
                      {pageData && arrayValidator(pageData.sales, 3) ? pageData?.sales[3]?.saleTitle : ""}
                    </Title>

                    <Link
                      href={
                        pageData && arrayValidator(pageData.sales, 3) ? `/${pageData?.sales[3]?.saleButtonLink}` : "/"
                      }
                      className="inline-flex items-center  text-system-disabled mt-[12px] xl:text-[1.11vw] hoverunderline hoverunderline-white"
                    >
                      {pageData && arrayValidator(pageData.sales, 3)
                        ? pageData?.sales[3]?.saleButtonText
                        : "Explore more"}
                      <ChevronRight className="ml-1  h-[17px] w-[15px] xl:h-[1.18vw] xl:w-[1.04vw]" />
                    </Link>
                  </div>
                  <Image
                    height={340}
                    width={360}
                    className="w-full h-full object-cover"
                    alt={pageData && arrayValidator(pageData.sales, 3) ? pageData?.sales[3]?.saleTitle : ""}
                    src={
                      pageData && arrayValidator(pageData.sales, 3) && pageData?.sales[3]?.image?.url
                        ? `${cmsUrl}/${pageData?.sales[3]?.image?.url ? pageData?.sales[3]?.image?.url : ""}`
                        : ""
                    }
                  />
                </Animate>
              </div>
            </div>
          </Animate>
        </ParallaxWithFramer>

        <NewsletterSection />
      </main>

      <Footer />
    </>
  );
}
